import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import {
  createSector,
  DestinationImageUpload,
  getSectorByID,
  updateSector,
} from "./services/sector.services";
import { clearSectorDetails } from "./sectorSlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { SEASON, SECTORTYPE, STATUSARRAY } from "../../_contstants/common";
import { IRegion } from "../region/regionModel";
import { ICountry } from "../country/countryModel";
import { getAllActiveCountries } from "../country/services/country.services";
import { getAllActiveRegions } from "../region/services/region.services";
import { ICity } from "../city/cityModel";
import { getAllActiveCities } from "../city/services/city.services";
import FormMultiSelectionFiled from "../../components/formFields/FormMultiSelectionFiled";
import { Typography } from "@progress/kendo-react-common";
import FormCheckbox from "../../components/formFields/FormCheckbox";
import { useLocation, useNavigate } from "react-router-dom";
import ShadowCard from "../../components/common/ShadowCard";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import FormTextArea from "../../components/formFields/FormTextArea";
import FormRichTextField from "../../components/formFields/FormRichTextField";

const CreateSector: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const gridRef = useRef<any>(null);
  const location = useLocation();

  const SectorDetail = useAppSelector((state) => state.sector.SectorDetail);
  const SectorID = location.state?.SectorID;
  const RegionList = useAppSelector((state) => state.region.RegionList);
  const CityList = useAppSelector((state) => state.city.CityList);
  const CountryList = useAppSelector((state) => state.country.CountryList);
  const loading = useAppSelector((state) => state.sector.loading);
  const [formKey, setFormKey] = React.useState(0);

  useEffect(() => {
    dispatch(getAllActiveCities());
    dispatch(getAllActiveRegions());
    dispatch(getAllActiveCountries());

    return () => {
      dispatch(clearSectorDetails());
    };
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [SectorDetail]);

  useEffect(() => {
    if (SectorID) {
      dispatch(getSectorByID(SectorID));
    }
  }, [SectorID]);

  const handleSubmit = async (values: any) => {
    const ImageData = new FormData();
    const SectorImage =
      values?.SectorImage && typeof values?.SectorImage !== "string"
        ? values.SectorImage[0].getRawFile()
        : "";
    const BannerImage =
      values?.BannerImage && typeof values?.BannerImage !== "string"
        ? values.BannerImage[0].getRawFile()
        : "";
    const HomeImage =
      values?.HomeImage && typeof values?.HomeImage !== "string"
        ? values.HomeImage[0].getRawFile()
        : "";

    try {
      if (SectorID) {
        const editPayload = {
          ID: SectorID,
          CityID: Array.isArray(values?.CityID)
            ? values?.CityID.join(",")
            : values?.CityID
            ? values?.CityID
            : "",
          Prefix: values?.Prefix ? values?.Prefix : "",
          RegionID: values?.RegionID ? +values?.RegionID : null,
          CountryID: values?.CountryID ? +values?.CountryID : null,
          SectorName: values?.SectorName ? values?.SectorName : "",
          SectorType: values?.SectorType ? values?.SectorType : "",
          Season: values?.Season ? values?.Season.join(",") : "",
          IsShowOnMenu: values?.IsShowOnMenu ? values?.IsShowOnMenu : false,
          IsShowOnHome: values?.IsShowOnHome ? values?.IsShowOnHome : false,
          Description: values.Description ? values.Description : "",
          LongDescription: values.LongDescription ? values.LongDescription : "",
          MetaTitle: values.MetaTitle ? values.MetaTitle : "",
          MetaKeyWord: values.MetaKeyWord ? values.MetaKeyWord : "",
          MetaDiscription: values.MetaDiscription ? values.MetaDiscription : "",
          IsActive: values?.IsActive === 1,
        };
        const response = await dispatch(updateSector(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.SectorImage && typeof values?.SectorImage !== "string") ||
            (values?.BannerImage && typeof values?.BannerImage !== "string") ||
            (values?.HomeImage && typeof values?.HomeImage !== "string")
          ) {
            ImageData.append("SectorImage", SectorImage);
            ImageData.append("BannerImage", BannerImage);
            ImageData.append("HomeImage", HomeImage);
            ImageData.append("ID", values?.ID ? values?.ID : "");

            await dispatch(DestinationImageUpload(ImageData));
          }
          navigate("/sector");
          dispatch(clearSectorDetails());
        }
      } else {
        const insertPayload = {
          CityID: Array.isArray(values?.CityID)
            ? values?.CityID.join(",")
            : values?.CityID
            ? values?.CityID
            : "",
          Prefix: values?.Prefix ? values?.Prefix : "",
          RegionID: values?.RegionID ? +values?.RegionID : null,
          CountryID: values?.CountryID ? +values?.CountryID : null,
          SectorName: values?.SectorName ? values?.SectorName : "",
          SectorType: values?.SectorType ? values?.SectorType : "",
          Season: values?.Season ? values?.Season.join(",") : "",
          IsShowOnMenu: values?.IsShowOnMenu ? values?.IsShowOnMenu : false,
          IsShowOnHome: values?.IsShowOnHome ? values?.IsShowOnHome : false,
          Description: values.Description ? values.Description : "",
          LongDescription: values.LongDescription ? values.LongDescription : "",
          MetaTitle: values.MetaTitle ? values.MetaTitle : "",
          MetaKeyWord: values.MetaKeyWord ? values.MetaKeyWord : "",
          MetaDiscription: values.MetaDiscription ? values.MetaDiscription : "",
          IsActive: values?.IsActive === 1,
        };
        const response = await dispatch(createSector(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            ((values?.SectorImage && typeof values?.SectorImage !== "string") ||
              (values?.HomeImage && typeof values?.HomeImage !== "string") ||
              (values?.BannerImage &&
                typeof values?.BannerImage !== "string")) &&
            response?.payload?.Data?.SectorID
          ) {
            ImageData.append("SectorImage", SectorImage);
            ImageData.append("BannerImage", BannerImage);
            ImageData.append("HomeImage", HomeImage);
            ImageData.append("ID", response?.payload?.Data?.SectorID);

            await dispatch(DestinationImageUpload(ImageData));
          }
          navigate("/sector");
          dispatch(clearSectorDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };
  const handleFormClose = () => {
    navigate("/sector");
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={SectorDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 20 }}
                gap={{ rows: 5, cols: 10 }}
                cols={[
                  { width: "33.33%" },
                  { width: "33.33%" },
                  { width: "33.33%" },
                ]}
              >
                {/* <GridLayoutItem
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                margin: "17px 0 8px",
              }}
            >
              <Typography.h4 className="m-0">
                {SectorID ? "Update Sector" : "Add Sector"}
              </Typography.h4>
              <IconButton
                type="button"
                fillMode={"flat"}
                themeColor={"dark"}
                style={{ marginRight: 4 }}
                onClick={() => dispatch(closed())}
              >
                             <RxCross1 />
                           </IconButton>
            </GridLayoutItem> */}
                <GridLayoutItem colSpan={3}>
                  <Typography.h4>
                    {SectorID ? "Update Sector" : "Add Sector"}
                  </Typography.h4>
                </GridLayoutItem>

                <GridLayoutItem style={{ width: "100%" }}>
                  <Field
                    name="SectorName"
                    label="Sector"
                    placeholder="Sector"
                    astrike={true}
                    component={FormTextField}
                    validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem style={{ width: "100%" }}>
                  <Field
                    name="Prefix"
                    label="Prefix"
                    placeholder="Prefix"
                    astrike={true}
                    component={FormTextField}
                    validator={requiredValidator}
                  />
                </GridLayoutItem>
                <GridLayoutItem style={{ width: "100%" }}>
                  <Field
                    name="SectorType"
                    label="Sector Type"
                    placeholder="Sector Type"
                    astrike={true}
                    component={FormSelectionField}
                    validator={requiredValidator}
                    options={SECTORTYPE?.map((sectorType: any) => {
                      return {
                        value: sectorType?.value,
                        label: sectorType?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem style={{ width: "100%" }}>
                  <Field
                    name="RegionID"
                    label="Region"
                    placeholder="Region"
                    component={FormSelectionField}
                    // astrike={true}
                    // validator={requiredValidator}
                    options={RegionList?.map((region: IRegion) => {
                      return {
                        value: region?.ID,
                        label: region?.RegionName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem style={{ width: "100%" }}>
                  <Field
                    name="CityID"
                    label="City"
                    placeholder="i.e. Ahmedabad"
                    component={FormMultiSelectionFiled}
                    options={CityList?.map((city: ICity) => {
                      return {
                        value: city?.ID,
                        label: city?.CityName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem style={{ width: "100%" }}>
                  <Field
                    name="CountryID"
                    label="Country"
                    placeholder="i.e. India"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={CountryList?.map((coountry: ICountry) => {
                      return {
                        value: coountry?.ID,
                        label: coountry?.CountryName,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem style={{ width: "100%" }}>
                  <div
                    style={{
                      marginTop: "18px",
                      background: "#edeaea",
                      borderRadius: 10,
                      padding: "15px 10px",
                      fontWeight: 500,
                      color: "black",
                    }}
                  >
                    <Field
                      wrapperClassName="m-0"
                      labelClassName="ms-2"
                      name="IsShowOnHome"
                      label="Is Show On Home"
                      maxLength="50"
                      component={FormCheckbox}
                    />
                  </div>
                </GridLayoutItem>
                <GridLayoutItem style={{ width: "100%" }}>
                  <div
                    style={{
                      marginTop: "18px",
                      background: "#edeaea",
                      borderRadius: 10,
                      padding: "15px 10px",
                      fontWeight: 500,
                      color: "black",
                    }}
                  >
                    <Field
                      wrapperClassName="m-0"
                      labelClassName="ms-2"
                      name="IsShowOnMenu"
                      label="Is Show On Menu"
                      maxLength="50"
                      component={FormCheckbox}
                    />
                  </div>
                </GridLayoutItem>
                <GridLayoutItem style={{ width: "100%" }}>
                  <Field
                    name="Season"
                    label="Season"
                    placeholder="Season"
                    component={FormMultiSelectionFiled}
                    options={SEASON?.map((season: any) => {
                      return {
                        value: season?.value,
                        label: season?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem style={{ width: "100%" }} colSpan={3}>
                  <Field
                    wrapperStyle={{ width: "100%" }}
                    style={{ height: 160 }}
                    name="Description"
                    label="Description"
                    component={FormRichTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem style={{ width: "100%" }} colSpan={3}>
                  <Field
                    wrapperStyle={{ width: "100%" }}
                    style={{ height: 200 }}
                    minHeight={300}
                    name="LongDescription"
                    label="Long Description"
                    component={FormRichTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem style={{ width: "100%" }}>
                  <Field
                    id={"IsActive"}
                    name={"IsActive"}
                    label={"Status"}
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    placeholder="i.e. Active"
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status.value,
                        label: status.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}></GridLayoutItem>
                <GridLayoutItem style={{ marginTop: 20 }}>
                  <Field
                    justifyContent="start"
                    name={`SectorImage`}
                    label="Main Image"
                    type="file"
                    accept="image/*"
                    alternateName="Main Image"
                    component={FormImageUpload}
                  />
                </GridLayoutItem>
                <GridLayoutItem style={{ marginTop: 20 }}>
                  <Field
                    justifyContent="start"
                    name={`BannerImage`}
                    label="Banner Image"
                    type="file"
                    accept="image/*"
                    alternateName="Banner Image"
                    component={FormImageUpload}
                  />
                </GridLayoutItem>
                <GridLayoutItem style={{ marginTop: 20 }}>
                  <Field
                    justifyContent="start"
                    name={`HomeImage`}
                    label="Home Image"
                    type="file"
                    accept="image/*"
                    alternateName="Home Image"
                    component={FormImageUpload}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={3}>
                  <hr />
                </GridLayoutItem>
                <GridLayoutItem colSpan={3}>
                  <Typography.h4 style={{ margin: 0 }}>
                    Meta Information
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem colSpan={3} style={{ width: "100%" }}>
                  <Field
                    name="MetaTitle"
                    label="Meta Title"
                    placeholder="Meta Title"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={3} style={{ width: "100%" }}>
                  <Field
                    name="MetaKeyWord"
                    label="Meta Keywords"
                    placeholder="Meta Keywords"
                    component={FormTextField}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={3}>
                  <Field
                    wrapperStyle={{ width: "100%" }}
                    name="MetaDiscription"
                    label="Meta Description"
                    component={FormTextArea}
                    rows={2}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  className="d-flex justify-content-end align-items-end mt-3"
                  colSpan={3}
                >
                  <ButtonWithLoading
                    label={SectorID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    style={{ marginLeft: 10 }}
                    onClick={handleFormClose}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateSector;
